import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { some } from 'lodash';

import { popupTypes } from 'helpers/constants';
import { getTranslatedKPINameV2 } from 'helpers/translateHelper';
import { GeneralDialog, LoadingIndicator, RadioButton } from 'sharedComponents';
import { getColumnConfigSuggestedLimits, getLimitValueRegex, limitTypes } from './helper';
import { LEVEL, LIMIT_SYMBOL } from './constants';
import { options } from './Limit';

const SuggestLimitModal = (props) => {
    const { t: translate } = useTranslation();
    const dispatch = useDispatch();
    const { model, close, actions } = props;
    const { selectedTimeSeriesForSuggestion, suggestedLimit } = model;

    const [selectedLimitType, setSelectedLimitType] = useState(limitTypes[1]);

    const mainTitle = `${translate('ABB.Powertrain.Frontend.suggestLimitTitle')} | ${getTranslatedKPINameV2(selectedTimeSeriesForSuggestion)}`;
    const noSuggestionsFoundTitle = `${translate('ABB.Powertrain.Frontend.noSuggestionsFoundTitle')} | ${getTranslatedKPINameV2(selectedTimeSeriesForSuggestion)}`;
    const columnConfig = getColumnConfigSuggestedLimits(translate, selectedTimeSeriesForSuggestion);
    const hasInvalidLimits = [selectedTimeSeriesForSuggestion]?.some(item =>
        some(item.limits, limit =>
            !limit.limitLevel || limit.limitValue === undefined || limit.limitValue === null || limit.limitValue === '' || !limit.limitDirection || limit.isSameLimit
        )
    );
    const isCurrentLimitModified = selectedLimitType === limitTypes[0] ? selectedTimeSeriesForSuggestion?.limits?.find(item => item?.isModified) : true;

    const handleCurrentUpdateLimit = (uniqId, field, value) => {
        dispatch(actions.updateCurrentLimit(uniqId, field, value));
    };

    const handleLimitTypeChange = (key) => {
        setSelectedLimitType(key);
    };

    const handleUpdateTimeSeries = () => {
        switch (selectedLimitType) {
            case limitTypes[0]: {// UPDATE CURRENT LIMIT
                const currentLimits = selectedTimeSeriesForSuggestion?.limits?.filter((lmt) => lmt?.hasCurrentLimit);
                const updatedTimeSeries = {
                    ...selectedTimeSeriesForSuggestion,
                    limits: currentLimits
                };
                dispatch(actions.updateSelectedTimeSeriesSuggestedData(updatedTimeSeries, selectedLimitType));
                break;
            }
            case limitTypes[1]: // UPDATE SUGGESTED LIMIT
                dispatch(actions.updateSelectedTimeSeriesSuggestedData(selectedTimeSeriesForSuggestion, selectedLimitType));
                break;
            default:
                break;
        }
        close();
    };

    const renderLimits = (limit) => {
        const isDisabled = selectedLimitType === limitTypes[1];
        const findIcon = options.find((o) => limit?.limitLevel === o.label);
        const suggestLimit = suggestedLimit?.limits?.find(
            (sl) => limit?.limitDirection === sl?.limitDirection && limit?.limitLevel === sl?.limitLevel
        );
        const renderLimitInput = (limitData, isSuggested = false) => {
            const isInputDisabled = isSuggested || isDisabled;
            const limitValueRegex = getLimitValueRegex(limitData?.precision ?? selectedTimeSeriesForSuggestion?.timeseriesPrecision ?? selectedTimeSeriesForSuggestion?.precision);
            const directionSymbol =
                limitData?.limitDirection === LEVEL.UpperLimit || limitData?.limitDirection === LEVEL.Upper
                    ? LIMIT_SYMBOL.Greater
                    : LIMIT_SYMBOL.Lower;
            return (
                <div className={`limit-value-uom ${isSuggested ? 'suggested-limit' : 'current-limit'} ${isInputDisabled ? 'disabled' : ''}`}>
                    <div className='suggest-limit-item-direction'>{directionSymbol}</div>
                    <input
                        type='number'
                        placeholder={translate('ABB.Powertrain.Frontend.inputValue')}
                        value={limitData?.limitValue === 0 ? '0' : limitData?.limitValue || ''}
                        onChange={(e) =>
                            !isInputDisabled &&
                            limitValueRegex.test(e.target.value) &&
                            handleCurrentUpdateLimit(limit?.uniqId, 'limitValue', e.target.value)
                        }
                        name={limit?.uniqId}
                        disabled={isInputDisabled}
                    />
                    <div className='uom-text'>{selectedTimeSeriesForSuggestion?.unit}</div>
                </div>
            );
        };
        return (
            <div className='suggest-limit-item' key={limit?.uniqId}>
                <div className='suggest-limit-item-title'>
                    {findIcon?.prefixIcon} {limit?.limitLevel}
                </div>
                {limit?.hasCurrentLimit ? renderLimitInput(limit) : <div className='flex-1'></div>}
                {suggestLimit ? renderLimitInput(suggestLimit, true) : <div className='flex-1'></div>}
            </div>
        );
    };

    return (
        <GeneralDialog
            id='suggested-limits-modal'
            modalClassName='suggested-limits-modal'
            notificationType={suggestedLimit?.isLoading ? null : !suggestedLimit?.data?.calculatedLimit && popupTypes.ERROR}
            show={model.showSuggestLimitModal}
            close={close}
            title={suggestedLimit?.isLoading ? '' : suggestedLimit?.data?.calculatedLimit ? mainTitle : noSuggestionsFoundTitle}
            cancelButtonProps={suggestedLimit?.data?.calculatedLimit && {
                text: translate('ABB.Powertrain.Frontend.cancelLabel'),
            }}
            closeButton={true}
            acceptButtonProps={suggestedLimit?.data?.calculatedLimit ? {
                text: translate('ABB.Powertrain.Frontend.apply'),
                onClick: () => handleUpdateTimeSeries(),
                disabled: !suggestedLimit?.data?.calculatedLimit || suggestedLimit?.isLoading || hasInvalidLimits || !isCurrentLimitModified,
            } : {
                text: translate('ABB.Powertrain.Frontend.closeBtnLbl'),
                onClick: () => close(),
            }}
        >
            <div>
                {suggestedLimit?.isLoading ? <LoadingIndicator /> :
                    suggestedLimit?.data?.calculatedLimit ?
                        <div className='suggest-limit-table'>
                            <div className='suggest-limit-header'>
                                {columnConfig.map((column) => {
                                    const isChecked = selectedLimitType === column.key;
                                    return column.visible && <div className={`header-item ${isChecked ? 'selected' : ''}`} key={column.key}>
                                        {column.selectable ?
                                            <RadioButton
                                                id={column.key}
                                                checked={isChecked}
                                                onClick={() => { handleLimitTypeChange(column.key); }}
                                                label={column.title}
                                            />
                                            : column.title}
                                    </div>;
                                })}
                            </div>
                            {selectedTimeSeriesForSuggestion?.limits?.length > 0 &&
                                selectedTimeSeriesForSuggestion?.limits?.filter((lmt) => !lmt?.isUserLimit)?.map(limit => renderLimits(limit))
                            }
                        </div>
                        : <> {translate('ABB.Powertrain.Frontend.noSuggestionsFoundMsg')} </>}
            </div>
        </GeneralDialog>
    );
};

SuggestLimitModal.propTypes = {
    close: PropTypes.func, // Function to close the modal
    model: PropTypes.shape({
        showSuggestLimitModal: PropTypes.bool, // Whether the modal is visible
        selectedTimeSeriesForSuggestion: PropTypes.shape({
            uniqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Unique identifier for the time series
            unit: PropTypes.string, // Unit of measurement
            precision: PropTypes.number, // Legacy precision for numeric validation
            timeseriesPrecision: PropTypes.number, // Timeseries precision for numeric validation
            limits: PropTypes.arrayOf(
                PropTypes.shape({
                    uniqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Unique identifier for the limit
                    limitValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Value of the limit
                    limitDirection: PropTypes.string, // Direction of the limit (e.g., 'UpperLimit', 'LowerLimit')
                    limitLevel: PropTypes.string, // Level of the limit (e.g., 'Alarm', 'Warning')
                    precision: PropTypes.number, // Precision override for this limit
                    isModified: PropTypes.bool, // Whether the limit is modified
                    hasCurrentLimit: PropTypes.bool, // Whether the limit has a current value
                    isUserLimit: PropTypes.bool, // Whether the limit is user-defined
                })
            ),
        }),
        suggestedLimit: PropTypes.shape({
            isLoading: PropTypes.bool, // Whether the suggested limit data is loading
            data: PropTypes.shape({
                calculatedLimit: PropTypes.bool, // Whether the suggested limit is calculated
            }),
            limits: PropTypes.arrayOf(
                PropTypes.shape({
                    limitDirection: PropTypes.string, // Direction of the limit
                    limitLevel: PropTypes.string, // Level of the limit
                    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Default value of the limit
                })
            ),
        }),
    }), // Model containing modal data
    actions: PropTypes.shape({
        updateCurrentLimit: PropTypes.func, // Function to update the current limit
        updateSelectedTimeSeriesSuggestedData: PropTypes.func, // Function to update the suggested time series data
    }), // Actions object containing functions
};

export default React.memo(SuggestLimitModal);
